import _ from 'lodash';
import {
  AuthenticationManager,
  DefaultAuthenticationManager
} from './AuthenticationManager';
import { isSystemAdmin } from 'helper/ActorHelper';
import {
  AddonFeatureType,
  AddonDependency,
  ADDONFEATURE
} from 'core/agency/AddonFeature';
import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';

export interface AddonFeatureManager {
  /**
   * Validate current actor addonFeature is enable and with about dependency addonFeature.
   */
  isAnyOutdoorCreativeTypeEnable(): boolean;
  isFeatureEnable(addonFeature: AddonFeatureType): boolean;
  isEveryFeaturesEnable(...addonFeatures: AddonFeatureType[]): boolean;
  isAnyFeatureEnable(...addonFeatures: AddonFeatureType[]): boolean;
  updateAddonFeature(addonFeatures: any): void;
  convertAgencyAddonFeatures(addonFeatures: []): string[];
  isAnyVideoRelatedCreativeTypeEnable(): boolean;
  readonly addonFeature: string[];
  readonly event: UpdateEventListener<AddonFeatureManager>;
  readonly videoRelatedCreativeTypes: AddonFeatureType[];
  readonly outdoorCreativeTypes: AddonFeatureType[];
}

export class DefaultAddonFeatureManager implements AddonFeatureManager {
  authManager: AuthenticationManager;
  modelAddonFeatures: string[];
  addonEvent: FireableUpdateEventListener<AddonFeatureManager>;
  videoRelatedCreativeTypes: AddonFeatureType[] = [
    ADDONFEATURE.CREATIVES.VIDEO,
    ADDONFEATURE.CREATIVES.THIRD_PARTY,
    ADDONFEATURE.CREATIVES.COMBO,
    ADDONFEATURE.CREATIVES.EDIMAX,
    ADDONFEATURE.CREATIVES.PIC_LONG,
    ADDONFEATURE.CREATIVES.PIC_SHORT,
    ADDONFEATURE.CREATIVES.PIC_EXTENDED,
    ADDONFEATURE.CREATIVES.PIC_BROAD,
    ADDONFEATURE.CREATIVES.SANDBOX_OUTDOOR,
    ADDONFEATURE.CREATIVES.FRIDGE_LONG,
    ADDONFEATURE.CREATIVES.FRIDGE_EXTENDED,
    ADDONFEATURE.CREATIVES.FRIDGE_BROAD,
    ADDONFEATURE.CREATIVES.OUTDOOR_LINE_MESSAGE,
    ADDONFEATURE.CREATIVES.LINE_MESSAGE,
    ADDONFEATURE.CREATIVES.HWACOM_SHORT,
    ADDONFEATURE.CREATIVES.HWACOM_LONG,
    ADDONFEATURE.CREATIVES.HWACOM_EXTENDED,
    ADDONFEATURE.CREATIVES.HWACOM_BROAD
  ];
  outdoorCreativeTypes: AddonFeatureType[] = [
    ADDONFEATURE.CREATIVES.EDIMAX,
    ADDONFEATURE.CREATIVES.PIC_LONG,
    ADDONFEATURE.CREATIVES.PIC_SHORT,
    ADDONFEATURE.CREATIVES.PIC_EXTENDED,
    ADDONFEATURE.CREATIVES.PIC_BROAD,
    ADDONFEATURE.CREATIVES.SANDBOX_OUTDOOR,
    ADDONFEATURE.CREATIVES.FRIDGE_LONG,
    ADDONFEATURE.CREATIVES.FRIDGE_EXTENDED,
    ADDONFEATURE.CREATIVES.FRIDGE_BROAD,
    ADDONFEATURE.CREATIVES.OUTDOOR_LINE_MESSAGE,
    ADDONFEATURE.CREATIVES.LINE_MESSAGE,
    ADDONFEATURE.CREATIVES.HWACOM_SHORT,
    ADDONFEATURE.CREATIVES.HWACOM_LONG,
    ADDONFEATURE.CREATIVES.HWACOM_EXTENDED,
    ADDONFEATURE.CREATIVES.HWACOM_BROAD
  ];

  constructor(
    authenticationManager: AuthenticationManager = new DefaultAuthenticationManager()
  ) {
    this.authManager = authenticationManager;
    this.modelAddonFeatures = [];
    this.addonEvent = new FireableUpdateEventListener<AddonFeatureManager>();
  }

  isFeatureEnable(toBeCheckedAddonFeature: AddonFeatureType): boolean {
    if (isSystemAdmin(this.authManager.actor)) {
      return false;
    }

    const hasAddonFeature = this.modelAddonFeatures.includes(
      toBeCheckedAddonFeature.toString()
    );
    if (hasAddonFeature && AddonDependency[toBeCheckedAddonFeature]) {
      return AddonDependency[toBeCheckedAddonFeature].every((addonFeature) => {
        return this.modelAddonFeatures.includes(addonFeature);
      });
    }
    return hasAddonFeature;
  }

  isEveryFeaturesEnable(...addonFeatures: AddonFeatureType[]): boolean {
    if (isSystemAdmin(this.authManager.actor)) {
      return false;
    }

    return addonFeatures.every((addonFeature) => {
      return this.isFeatureEnable(addonFeature);
    });
  }

  isAnyFeatureEnable(...addonFeatures: AddonFeatureType[]): boolean {
    if (isSystemAdmin(this.authManager.actor)) {
      return false;
    }

    return addonFeatures.some((addonFeature) => {
      return this.isFeatureEnable(addonFeature);
    });
  }

  convertAgencyAddonFeatures(addonFeatures: []): string[] {
    const addons = Object.entries(
      _.omit(addonFeatures, ['campaignLimitation', 'creativeLimitation'])
    ).reduce((acc: string[], [category, addonFeature]) => {
      if (!addonFeature || typeof addonFeature !== 'object') {
        return acc;
      }
      Object.keys(addonFeature).forEach((addonKey: string) => {
        const value = (addonFeature as any)[addonKey];
        if (!value) return;
        // per-channel flat composite key：addonFeatures.<category>.channel_${id}_${subKey}
        // 攤平為 scoped key `${category}_channel_${id}_${subKey}`，供 isFeatureEnable
        // 直接做陣列比對。其他 root-level flat key 維持原 push（legacy 行為）。
        if (addonKey.startsWith('channel_')) {
          acc.push(`${category}_${addonKey}`);
        } else {
          acc.push(addonKey);
        }
      });
      return acc;
    }, []);
    if ('campaignLimitation' in addonFeatures) {
      // Flat per-channel-group keys:
      // campaignLimitation[`channel_${channelGroupId}_${featureKey}`] = boolean.
      // Prefixed to the scoped key `campaign_channel_${channelGroupId}_${featureKey}`.
      const campaignLimitation: any = addonFeatures['campaignLimitation'] ?? {};
      Object.keys(campaignLimitation)
        .filter((key) => campaignLimitation[key])
        .forEach((key: string) => {
          addons.push(`campaign_${key}`);
        });
    }

    if ('creativeLimitation' in addonFeatures) {
      const creativeLimitation: any = addonFeatures['creativeLimitation'];
      Object.keys(creativeLimitation)
        .filter((addonKey) => {
          return creativeLimitation[addonKey];
        })
        .forEach((addonKey: string) => {
          addons.push(`creative_${addonKey}`);
        });
    }

    return addons;
  }

  notifyListeners() {
    this.addonEvent.fireEvent(this);
  }

  get addonFeature(): string[] {
    return this.modelAddonFeatures;
  }

  get event(): UpdateEventListener<AddonFeatureManager> {
    return this.addonEvent;
  }

  updateAddonFeature(addonFeatures: any): void {
    const converedAddonFeatures =
      this.convertAgencyAddonFeatures(addonFeatures);
    this.modelAddonFeatures = converedAddonFeatures;
    this.notifyListeners();
  }

  isAnyOutdoorCreativeTypeEnable(): boolean {
    return this.isAnyFeatureEnable(...this.outdoorCreativeTypes);
  }

  isAnyVideoRelatedCreativeTypeEnable(): boolean {
    return this.isAnyFeatureEnable(...this.videoRelatedCreativeTypes);
  }
}
